import {tokens} from '@pleo-io/telescope'
import VisuallyHidden from '@reach/visually-hidden'
import React from 'react'
import styled from 'styled-components'

import ExternalLink from '@helios/shared/components/external-link'
import LocaleSwitcher from '@helios/shared/components/locale-switcher'
import PrismicLink, {Props as PrismicLinkProps} from '@helios/shared/components/prismic-link'
import RichText, {Props as RichTextProps} from '@helios/shared/components/rich-text'
import logoFooterIcon from '@helios/shared/images/icons/logo-horizontal.svg'
import {socialLinks} from '@helios/shared/site-config'
import {gutters} from '@helios/shared/styles/grid'
import {Text, TextLink, NavTextLink} from '@helios/shared/styles/typography'
import {colors} from '@helios/shared/theme'
import Locale from '@helios/shared/types/locale'
import mq from '@helios/shared/utils/media-query'

type FooterProps = {
    locales?: Locale[]
    content: {
        entity: string
        address: RichTextProps['content']
        cvr: string
        terms_body: string
        copyright_text: string
        sales_email: string
        info_email: string
        support_email: string
        product_heading: string
        resources_heading: string
        company_heading: string
        legal_heading: string
        product_links: Link[]
        resources_links: Link[]
        company_links: Link[]
        legal_links: Link[]
    }
}

export const Footer = ({content, locales}: FooterProps) => {
    return (
        <Wrapper>
            <Details>
                <Logo src={logoFooterIcon} alt="" />

                <Text as={Address} color="muted">
                    <span>
                        {content.entity}
                        <br />
                    </span>

                    <RichText content={content.address} />

                    <span>CVR: {content.cvr}</span>
                </Text>

                <LocaleSwitcher locales={locales} light />

                <SocialLinks>
                    {socialLinks.map((link) => (
                        <ExternalLink key={link.name} to={link.url}>
                            <SocialIcon as={link.icon} width={24} height={24} aria-hidden />

                            <VisuallyHidden>{link.name}</VisuallyHidden>
                        </ExternalLink>
                    ))}
                </SocialLinks>

                <StyledLinkGroup>
                    <TextLink bgColor="black" as="a" href={`mailto:${content.sales_email}`}>
                        {content.sales_email}
                    </TextLink>
                    <TextLink bgColor="black" as="a" href={`mailto:${content.info_email}`}>
                        {content.info_email}
                    </TextLink>
                    <TextLink bgColor="black" as="a" href={`mailto:${content.support_email}`}>
                        {content.support_email}
                    </TextLink>
                </StyledLinkGroup>
            </Details>

            <Links>
                {!!content.product_links.length && (
                    <LinkGroup heading={content.product_heading} links={content.product_links} />
                )}
                {!!content.resources_links.length && (
                    <LinkGroup
                        heading={content.resources_heading}
                        links={content.resources_links}
                    />
                )}
                {!!content.company_links.length && (
                    <LinkGroup heading={content.company_heading} links={content.company_links} />
                )}
                {!!content.legal_links.length && (
                    <LinkGroup heading={content.legal_heading} links={content.legal_links} />
                )}

                <Terms>
                    <Text color="muted" size="xs" font="secondary">
                        {content.terms_body}
                    </Text>

                    <Text color="muted" size="xs" font="secondary">
                        {content.entity}, {new Date().getFullYear()}. {content.copyright_text}
                    </Text>
                </Terms>
            </Links>
        </Wrapper>
    )
}

type Link = {
    text: string
    link: PrismicLinkProps
}

type LinkGroupProps = {
    heading: string
    links: Link[]
}

const LinkGroup = ({heading, links}: LinkGroupProps) => (
    <StyledLinkGroup>
        <SectionHeading>
            <Text as="h3" size="xs" color="muted" upperCase font="secondary">
                {heading}
            </Text>
        </SectionHeading>

        {links?.map((item, i) => (
            <NavTextLink as={PrismicLink} key={i} {...item?.link}>
                {item.text}
            </NavTextLink>
        ))}
    </StyledLinkGroup>
)

export const Section = styled.section`
    display: grid;
    row-gap: ${tokens.spacing28};
    column-gap: ${tokens.spacing8};
    align-content: start;
`

export const Wrapper = styled(Section).attrs({as: 'footer'})`
    ${gutters};
    padding-top: ${tokens.spacing60};
    padding-bottom: ${tokens.spacing60};
    background: ${colors.background.secondary};

    ${mq.min('sm')} {
        grid-template-columns: repeat(12, 1fr);
    }

    ${mq.min('md')} {
        padding-top: 120px;
        padding-bottom: 120px;
    }
`

export const Logo = styled.img`
    width: 80px;

    ${mq.min('md')} {
        width: 90px;
    }
`

export const SocialLinks = styled.nav`
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: start;
    column-gap: ${tokens.spacing8};

    ${mq.min('sm')} {
        order: 1;
    }

    > * {
        display: flex;
    }
`

const SocialIcon = styled.div`
    color: ${tokens.shade700};
    transition: ${tokens.fastIn};
    transition-property: color;

    a:hover & {
        color: ${tokens.shade900};
    }
`

const Details = styled(Section)`
    grid-column: 1 / span 3;
    align-content: start;
`

const Links = styled(Section)`
    grid-template-columns: repeat(2, 1fr);
    grid-column: 4 / -1;

    ${mq.min('sm')} {
        grid-template-columns: repeat(4, 1fr);
    }
`

const Terms = styled(Section)`
    grid-column: 1 / -1;
    margin-top: ${tokens.spacing40};
    row-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        margin-top: 0;
    }
`

const Address = styled.div`
    ${mq.min('sm')} {
        order: 2;
    }
`

const SectionHeading = styled.div`
    margin-bottom: ${tokens.spacing4};
    letter-spacing: 0.2em;
`

const StyledLinkGroup = styled.nav`
    display: grid;
    grid-template-columns: auto;
    row-gap: ${tokens.spacing8};
    align-content: start;
    justify-items: start;
`
